import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionOccurrence } from "../../features/sessions/models/SessionOccurrence";
import Button from "../button/Button";
import ModalShell from "../modal/ModalShell";
import { formatDateDisplay } from "../../features/sessions/utils/DisplayMappers";

export type OverrideMode = "cancel" | "reschedule" | "retime";

export interface OverridePayload {
  newDate?: string; // YYYY-MM-DD (reschedule)
  newTime?: string; // HH:mm (reschedule / retime)
  reason?: string;
}

interface OccurrenceOverrideDialogProps {
  mode: OverrideMode;
  occurrence: SessionOccurrence;
  busy: boolean;
  error: string | null;
  onConfirm: (payload: OverridePayload) => void;
  onClose: () => void;
}

/**
 * Modal for a per-date override: cancel, reschedule (new date + time), or
 * re-time (new time on the same date). Affects only the chosen occurrence.
 */
const OccurrenceOverrideDialog: React.FC<OccurrenceOverrideDialogProps> = ({
  mode,
  occurrence,
  busy,
  error,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  const [newDate, setNewDate] = useState(occurrence.effectiveDate);
  const [newTime, setNewTime] = useState(occurrence.effectiveTime);
  const [reason, setReason] = useState("");

  const date = formatDateDisplay(occurrence.effectiveDate);
  const titleByMode: Record<OverrideMode, string> = {
    cancel: t("sessions.occurrenceList.cancelTitle", { date }),
    reschedule: t("sessions.occurrenceList.rescheduleTitle", { date }),
    retime: t("sessions.occurrenceList.retimeTitle", { date }),
  };

  const canConfirm =
    mode === "cancel" ||
    (mode === "retime" && newTime.length > 0) ||
    (mode === "reschedule" && newDate.length > 0 && newTime.length > 0);

  const handleConfirm = () => {
    onConfirm({
      newDate: mode === "reschedule" ? newDate : undefined,
      newTime: mode !== "cancel" ? newTime : undefined,
      reason: reason.trim() || undefined,
    });
  };

  return (
    <ModalShell onClose={onClose} labelledBy="occurrence-override-title">
      <div className="p-4 lg:p-6">
        <h3 id="occurrence-override-title" className="text-grey-10 dark:text-grey-90">
          {titleByMode[mode]}
        </h3>

        <div className="mt-4 space-y-3">
          {mode === "cancel" && occurrence.visitCount > 0 && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t("sessions.occurrenceList.cancelClearsAttendance")}
            </p>
          )}

          {mode === "reschedule" && (
            <label className="block">
              <span className="text-sm text-grey-30 dark:text-grey-70">
                {t("sessions.occurrenceList.newDate")}
              </span>
              <input
                type="date"
                className="input mt-1 w-full"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
            </label>
          )}

          {mode !== "cancel" && (
            <label className="block">
              <span className="text-sm text-grey-30 dark:text-grey-70">
                {t("sessions.occurrenceList.newTime")}
              </span>
              <input
                type="time"
                className="input mt-1 w-full"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
              />
            </label>
          )}

          <label className="block">
            <span className="text-sm text-grey-30 dark:text-grey-70">
              {t("sessions.occurrenceList.reason")}
            </span>
            <input
              type="text"
              className="input mt-1 w-full"
              value={reason}
              maxLength={500}
              placeholder={t("sessions.occurrenceList.reasonPlaceholder")}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {mode === "cancel" ? t("sessions.occurrenceList.keepDate") : t("common.cancel")}
          </Button>
          {mode === "cancel" ? (
            <Button variant="danger" onClick={handleConfirm} disabled={busy}>
              {t("sessions.occurrenceList.confirmCancel")}
            </Button>
          ) : (
            <Button onClick={handleConfirm} disabled={busy || !canConfirm}>
              {t("common.save")}
            </Button>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default OccurrenceOverrideDialog;
